import { useUnit } from 'effector-react'
import { useQueryClient } from 'react-query'

import { CreatorsListDialog } from '@/components/ui/creators-list-dialog.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { $creators, loadCreatorsFx } from '@/models/creators.ts'
import type { Creator } from '@/modules/creators/types'
import { handleError } from '@/utils/error'

import { useConnect } from '../../hooks/use-connect-creator.ts'

export interface ConnectCreatorDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  licenseId: number
  planDisplayName: string
}

export const ConnectCreatorDialog = (props: ConnectCreatorDialogProps) => {
  const { open, setOpen, planDisplayName, licenseId } = props
  const creators = useUnit($creators)
  const connect = useConnect()
  const toast = useToast()
  const queryClient = useQueryClient()
  const availableCreators = creators.filter((creator) => !creator.licenseId)

  const handleSubmit = (selectedCreator: Creator[]) => {
    return connect
      .mutateAsync({
        creatorId: selectedCreator[0].id,
        licenseId: licenseId,
      })
      .then(() => toast.success('Successfully connected license to creator'))
      .catch((error) =>
        handleError(error, 'Failed to connect license to creator.'),
      )
      .finally(() => {
        loadCreatorsFx()
        connect.reset()
        queryClient.invalidateQueries('licenses')
        queryClient.invalidateQueries('creator')
      })
  }

  const handleClose = () => setOpen(false)

  return (
    <CreatorsListDialog
      open={open}
      onClose={handleClose}
      creators={availableCreators}
      title="Connect Creator"
      noCreatorMessage="There are no creators added to your account who are not linked to any license"
      description={
        <>
          Select the creator that should be connected to the{' '}
          <span className="text-title">{planDisplayName} License</span>
        </>
      }
      onSubmit={handleSubmit}
      submitButtonLabel="Connect"
      isSubmitButtonLoading={connect.isLoading}
    />
  )
}
